import { useEffect, useRef } from "react";

/** Изчакванията преди всеки повторен опит (нарастващи); след последния —
 *  край, без безкраен цикъл от заявки. */
export const USER_INFO_RETRY_DELAYS_MS: readonly number[] = [2000, 4000, 8000, 16000];

interface UserInfoRetryOptions {
  /** `auth.status` от Redux. */
  status: "idle" | "loading" | "succeeded" | "failed";
  /** Вече има заредени роли — тогава нищо не се повтаря (провалено
   *  опресняване ги пази и status остава „succeeded"). */
  hasRoles: boolean;
  /** Нова заявка за самоличността (`dispatch(fetchUserInfo())`). */
  retry: () => void;
}

/**
 * Повторен опит за `GET /user/info` на екрана с модулите. Без него първото
 * зареждане при недостъпен сървър оставаше „failed" без роли — празен екран
 * без нито една карта до изход (ефектът там тръгва само от „idle").
 *
 * Само при „failed" БЕЗ роли: до `USER_INFO_RETRY_DELAYS_MS.length` опита с
 * нарастващо изчакване. Успех (дойдат роли) нулира броенето — следващ провал
 * пак получава всичките опити. Затворен екран спира чакащия опит.
 */
export const useUserInfoRetry = ({ status, hasRoles, retry }: UserInfoRetryOptions) => {
  const attemptsRef = useRef(0);
  const retryRef = useRef(retry);
  retryRef.current = retry;

  useEffect(() => {
    if (hasRoles) {
      attemptsRef.current = 0;
      return;
    }
    if (status !== "failed") return;
    const attempt = attemptsRef.current;
    if (attempt >= USER_INFO_RETRY_DELAYS_MS.length) return;
    const timer = setTimeout(() => {
      attemptsRef.current = attempt + 1;
      retryRef.current();
    }, USER_INFO_RETRY_DELAYS_MS[attempt]);
    return () => clearTimeout(timer);
  }, [status, hasRoles]);
};
